import { reaction } from "mobx";
import type { MapStore } from "@/components/Map/stores/MapStore";
import type { MapPopupsStore } from "@/components/Map/MapPopups/MapPopupsStore";

/**
 * Wires the standard hover-preview + click-to-pin behavior between a generic
 * MapStore's markers layer and a MapPopupsStore. Also keeps the popups store
 * pruned to the current marker set (so pinned popups for removed markers
 * disappear, e.g. after a perspective switch or filter change).
 *
 * Returns a disposer for the pruning reaction. The mapbox event handlers stay
 * bound for the lifetime of the markers layer.
 */
export const wireMarkerPopups = (mapStore: MapStore<any>, popupsStore: MapPopupsStore): (() => void) => {
	mapStore.markers
		.onMarkerEnter(({ map }) => {
			(map.getCanvas() as HTMLCanvasElement).style.cursor = "pointer";
			popupsStore.keepHover();
		})
		.onMarkerMouseLeave(({ map }) => {
			(map.getCanvas() as HTMLCanvasElement).style.cursor = "";
			popupsStore.scheduleHoverClear(160);
		})
		.onMarkerMouseMove(({ id, lng, lat }) => {
			if (!id) {
				popupsStore.scheduleHoverClear(160);
				return;
			}
			const idStr = String(id);
			if (popupsStore.pinned.has(idStr)) {
				popupsStore.clearHover();
				return;
			}
			if (popupsStore.hover?.id === idStr) popupsStore.moveHover(lng, lat);
			else popupsStore.setHover(idStr, lng, lat);
		})
		.onMarkerClick(({ id, lng, lat }) => {
			if (!id) return;
			if (mapStore.connectionsStore.previewStore.selectedFromMarkerStructureId) {
				mapStore.connectionsStore.createConnection(String(id));
				return;
			}
			popupsStore.togglePin(String(id), lng, lat);
		});

	return reaction(
		() => mapStore.getMarkerIds(),
		markerIds => {
			const validIds = new Set(markerIds);
			popupsStore.prunePinned(validIds);
			popupsStore.ensureHoverIsValid(validIds);
		},
	);
};
